// core/nav-core.js
import { Alpine } from './alpine-core.js';
import { viteEnv } from './import-meta-env.js';
import { apiFetch } from '../admin/api.js';
import { getLeagueIdFromPath, withLeagueId } from '../admin/league-context.js';

const norm = (s) =>
  (s || '/')
    .split('?')[0]
    .split('#')[0]
    .replace(/^\/admin\/l\/[^/]+/i, '/admin')
    .replace(/\/+$/, '')
    .replace(/^\/admin\b/, '') || '/';
const LEAGUE_ID_KEY = 'dm.admin.league_id';
const LEAGUE_BOOKMARKS_KEY = 'dm.admin.league_bookmarks'; // legacy storage key
const LEAGUE_FAVORITES_KEY = 'dm.admin.league_favorites';
const LEAGUE_RECENTS_KEY = 'dm.admin.league_recent';
const DEFAULT_LEAGUE = 'PDL';

function isLeagueScopedAdminTarget(targetPath = '') {
  const target = String(targetPath || '').trim();
  return (
    target === '/fixtures' ||
    target.startsWith('/fixtures/') ||
    target === '/league-table' ||
    target.startsWith('/league-table/') ||
    target === '/league' ||
    target.startsWith('/league/')
  );
}

/** True when the current URL is under `/admin/l/:leagueId` (detail, fixtures, table, etc.). */
function isLeaguePrefixedAdminPath(pathname = '') {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  return /^\/admin\/l\/[^/]+(\/.*)?$/i.test(p);
}

function sanitizeLeagueId(value) {
  const s = String(value || '').trim();
  return /^[A-Za-z0-9_-]{1,24}$/.test(s) ? s.toUpperCase() : '';
}

function readList(key) {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || '[]');
    if (!Array.isArray(raw)) return [DEFAULT_LEAGUE, 'DEV'];
    const cleaned = raw.map(sanitizeLeagueId).filter(Boolean);
    return cleaned.length ? [...new Set(cleaned)] : [DEFAULT_LEAGUE, 'DEV'];
  } catch {
    return [DEFAULT_LEAGUE, 'DEV'];
  }
}

/** Same defaults as `readList` when the legacy bookmarks key is absent or empty — used only to migrate into favorites. */
function readLegacyBookmarkLeagues() {
  return readList(LEAGUE_BOOKMARKS_KEY);
}

function readFavorites() {
  try {
    const rawStored = localStorage.getItem(LEAGUE_FAVORITES_KEY);
    if (rawStored != null) {
      const raw = JSON.parse(rawStored);
      if (!Array.isArray(raw)) return [];
      const cleaned = raw.map(sanitizeLeagueId).filter(Boolean);
      return [...new Set(cleaned)];
    }
  } catch {
    /* fall through to migration */
  }
  // New key never written: migrate from legacy bookmarks (same behaviour as before).
  return readLegacyBookmarkLeagues();
}

function saveList(key, items) {
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // ignore write failures
  }
}

function registerKioskEntitlementStore() {
  Alpine.store('kioskEntitlement', {
    loaded: false,
    allowed: false,
    _inflight: null,
    async refresh() {
      if (this._inflight) return this._inflight;
      this._inflight = (async () => {
        try {
          const S = Alpine.store('session');
          if (!S?.token) {
            this.allowed = false;
            return;
          }
          const res = await apiFetch('/api/admin/account-settings');
          if (!res.ok) {
            this.allowed = false;
            return;
          }
          const data = await res.json().catch(() => ({}));
          this.allowed = data?.controlPlane?.entitlement?.kioskAllowed === true;
        } catch {
          this.allowed = false;
        } finally {
          this.loaded = true;
        }
      })();
      try {
        await this._inflight;
      } finally {
        this._inflight = null;
      }
    },
  });
}

export function registerNavController() {
  registerKioskEntitlementStore();
  Alpine.data('navController', () => ({

    path: globalThis.location.pathname,
    prefix: globalThis.location.pathname.startsWith('/admin') ? '/admin' : '',
    leagueId: DEFAULT_LEAGUE,
    leagueFavorites: readFavorites(),
    leagueRecent: readList(LEAGUE_RECENTS_KEY),
    leaguePickerOpen: false,
    leagueSearch: '',
    playerTeamPickerOpen: false,

    /** From `/dashboard-layout/meta` — same source as dashboard league tabs. */
    metaLeagueOptions: [],
    metaLeaguesLoaded: false,

    venueKioskUrl() {
      const base = String(viteEnv.BASE_URL || '/').replace(/\/$/, '');
      return `${base}/venue-kiosk`;
    },

    init() {
      const sync = (p) => {
        const curr = p ?? globalThis.location.pathname;
        this.path = curr;
        this.prefix = curr.startsWith('/admin') ? '/admin' : '';
        this.leaguePickerOpen = false;
        this.playerTeamPickerOpen = false;
        this.syncLeagueFromUrl();
      };

      // Initial sync
      sync();

      if (this.prefix === '/admin') {
        queueMicrotask(() => Alpine.store('kioskEntitlement').refresh());
        queueMicrotask(() => this.refreshMetaLeagues());
      }

      // Update when Pinecone finishes a navigation
      document.addEventListener('pinecone:end', () => {
        // If you prefer, you can also read from Pinecone history when available:
        // const curr = window.PineconeRouter?.history?.entries?.[window.PineconeRouter.history.index]?.path;
        // sync(curr || window.location.pathname);
        sync();
        if (this.prefix === '/admin') this.refreshMetaLeagues();
      });

      // Also handle browser back/forward (in case)
      globalThis.addEventListener('popstate', () => sync());
    },

    syncLeagueFromUrl() {
      if (this.prefix !== '/admin') return;
      const fromPath = sanitizeLeagueId(getLeagueIdFromPath(globalThis.location.pathname));
      const fromStorage = sanitizeLeagueId(localStorage.getItem(LEAGUE_ID_KEY)) || DEFAULT_LEAGUE;
      const league = fromPath || fromStorage;
      this.leagueId = league;
      this.rememberLeague(league);
      try {
        localStorage.setItem(LEAGUE_ID_KEY, league);
      } catch {
        // ignore write failures
      }
    },

    rememberLeague(id) {
      const clean = sanitizeLeagueId(id);
      if (!clean) return;
      this.leagueRecent = [clean, ...this.leagueRecent.filter((x) => x !== clean)].slice(0, 16);
      saveList(LEAGUE_RECENTS_KEY, this.leagueRecent);
    },

    metaLeagueKeys() {
      return (this.metaLeagueOptions || [])
        .map((o) => sanitizeLeagueId(o?.leagueKey || o?.key || o?._id))
        .filter(Boolean);
    },

    leagueOptionLabel(id = this.leagueId) {
      const clean = sanitizeLeagueId(id);
      if (!clean) return '';
      const opt = (this.metaLeagueOptions || []).find(
        (o) => sanitizeLeagueId(o?.leagueKey || o?.key || o?._id) === clean,
      );
      const label = String(opt?.label || opt?.name || '').trim();
      return label && label.toUpperCase() !== clean ? `${label} (${clean})` : clean;
    },

    allLeagueOptions() {
      const merged = [
        ...this.metaLeagueKeys(),
        this.leagueId,
        ...this.leagueRecent,
        ...this.leagueFavorites,
      ]
        .map(sanitizeLeagueId)
        .filter(Boolean);
      const unique = [...new Set(merged)].sort((a, b) => a.localeCompare(b));
      const q = sanitizeLeagueId(this.leagueSearch);
      if (!q) return unique;
      return unique.filter((id) => id.includes(q));
    },

    favoriteLeagueOptions() {
      const all = this.allLeagueOptions();
      const favorites = this.leagueFavorites.filter((id) => all.includes(id));
      const q = sanitizeLeagueId(this.leagueSearch);
      if (!q) return favorites;
      return favorites.filter((id) => id.includes(q));
    },

    isFavoriteLeague(id = this.leagueId) {
      const clean = sanitizeLeagueId(id);
      return !!clean && this.leagueFavorites.includes(clean);
    },

    async refreshMetaLeagues() {
      if (this.prefix !== '/admin') return;
      try {
        const res = await apiFetch('/dashboard-layout/meta');
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(String(j?.message || res.statusText));
        this.metaLeagueOptions = Array.isArray(j.leagueOptions) ? j.leagueOptions : [];
      } catch {
        this.metaLeagueOptions = [];
      } finally {
        this.metaLeaguesLoaded = true;
      }
    },

    /** True when the account has exactly one league in meta — header shows a label, not a picker. */
    singleMetaLeagueMode() {
      return this.metaLeaguesLoaded && this.metaLeagueKeys().length <= 1;
    },

    singleMetaLeagueDisplayName() {
      const o = this.metaLeagueOptions[0];
      if (!o) return this.leagueId;
      const label = String(o.label || '').trim();
      const key = String(o.leagueKey || '').trim().toUpperCase();
      return label || key || this.leagueId;
    },

    toggleLeaguePicker() {
      if (this.singleMetaLeagueMode()) return;
      this.leaguePickerOpen = !this.leaguePickerOpen;
      this.leagueSearch = '';
    },

    playerTeams() {
      const S = Alpine.store('session');
      return S?.playerTeams || [];
    },

    hasPlayerTeams() {
      const S = Alpine.store('session');
      return !!S?.hasPlayerTeams;
    },

    singlePlayerTeamMode() {
      return this.playerTeams().length === 1;
    },

    singlePlayerTeamDisplayName() {
      const t = this.playerTeams()[0];
      return t?.name || t?.id || 'My team';
    },

    teamViewHref(teamId) {
      const id = String(teamId || '').trim();
      if (!id) return '/admin/teams';
      return `/admin/teams/${encodeURIComponent(id)}`;
    },

    primaryPlayerTeamHref() {
      const teams = this.playerTeams();
      if (!teams.length) return '/admin/teams';
      return this.teamViewHref(teams[0].id || teams[0]._id);
    },

    togglePlayerTeamPicker() {
      if (this.singlePlayerTeamMode()) return;
      this.playerTeamPickerOpen = !this.playerTeamPickerOpen;
    },

    isPlayerTeamViewActive(teamId = '') {
      const raw = String(globalThis.location.pathname || '').replace(/\/+$/, '') || '/';
      const id = String(teamId || '').trim();
      if (id) {
        const esc = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`^/admin/teams/${esc}(?:/|$)`, 'i').test(raw) && !/\/edit(\/|$)/.test(raw);
      }
      return /^\/admin\/teams\/[^/]+(?:\/|$)/i.test(raw) && !/\/edit(\/|$)/.test(raw);
    },

    isAnyPlayerTeamViewActive() {
      const teams = this.playerTeams();
      if (!teams.length) return false;
      return teams.some((t) => this.isPlayerTeamViewActive(t.id || t._id));
    },

    toggleFavoriteLeague(id = this.leagueId) {
      const clean = sanitizeLeagueId(id);
      if (!clean) return;
      if (this.leagueFavorites.includes(clean)) {
        this.leagueFavorites = this.leagueFavorites.filter((x) => x !== clean);
      } else {
        this.leagueFavorites = [clean, ...this.leagueFavorites].slice(0, 16);
      }
      saveList(LEAGUE_FAVORITES_KEY, this.leagueFavorites);
      this.rememberLeague(clean);
    },

    useTypedLeague() {
      const clean = sanitizeLeagueId(this.leagueSearch);
      if (!clean) return;
      this.setLeague(clean);
    },

    /** Build an admin-shell path. Named `navHref` — do not use `href` (collides with Alpine `:href`). */
    navHref(h) {
      const target = h.replace(/^\/admin\b/, '');
      const base = `${this.prefix}${target === '/' ? '' : target}`;
      if (this.prefix !== '/admin') return base;
      if (target === '/fixtures' || target.startsWith('/fixtures/')) {
        return this.fixturesHref(this.leagueId, target);
      }
      if (target === '/league-table' || target.startsWith('/league-table/')) {
        return this.leagueTableHref(this.leagueId, target);
      }
      if (target === '/league' || target.startsWith('/league/')) {
        return this.leagueDetailHref(this.leagueId);
      }
      if (!isLeagueScopedAdminTarget(target)) {
        return base;
      }
      const league = sanitizeLeagueId(this.leagueId);
      if (!league) return base;
      return withLeagueId(base, league);
    },

    fixturesHref(leagueId = this.leagueId, subpath = '/fixtures') {
      const clean = sanitizeLeagueId(leagueId);
      const rest = String(subpath || '/fixtures').replace(/^\/admin\b/, '');
      if (!clean) return `/admin${rest === '/' ? '' : rest}`;
      return `/admin/l/${encodeURIComponent(clean)}${rest === '/' ? '' : rest}`;
    },

    leagueTableHref(leagueId = this.leagueId, subpath = '/league-table') {
      const clean = sanitizeLeagueId(leagueId);
      const rest = String(subpath || '/league-table').replace(/^\/admin\b/, '');
      if (!clean) return `/admin${rest === '/' ? '' : rest}`;
      return `/admin/l/${encodeURIComponent(clean)}${rest === '/' ? '' : rest}`;
    },

    leagueDetailHref(leagueId = this.leagueId) {
      const clean = sanitizeLeagueId(leagueId);
      return clean ? `/admin/l/${encodeURIComponent(clean)}` : '/admin/dashboard';
    },

    /** @deprecated Use leagueDetailHref — league home is `/admin/l/:id`, not dashboard tab. */
    leagueDashboardHref(leagueId = this.leagueId) {
      return this.leagueDetailHref(leagueId);
    },

    isLeagueDetailActive() {
      const full = String(globalThis.location.pathname || '').replace(/\/+$/, '') || '/';
      return /^\/admin\/l\/[^/]+$/i.test(full);
    },

    /** Dashboard tab deep-link (widgets); not used in header — header League → leagueDetailHref. */
    dashboardLeagueHref(leagueId) {
      const clean = sanitizeLeagueId(leagueId);
      return clean
        ? `/admin/dashboard?tab=${encodeURIComponent(`league:${clean}`)}`
        : this.dashboardsHref();
    },

    isLeagueDashboardActive() {
      const raw = String(globalThis.location.pathname || '').replace(/\/+$/, '') || '/';
      if (!raw.startsWith('/admin/dashboard')) return false;
      try {
        const tab = new URLSearchParams(globalThis.location?.search || '').get('tab');
        const rawTab = String(tab || '').trim();
        if (rawTab === 'account') return false;
        if (rawTab) {
          const m = /^league:([a-z0-9_-]{1,24})$/i.exec(rawTab) || /^([a-z0-9_-]{1,24})$/i.exec(rawTab);
          if (m) return sanitizeLeagueId(m[1]) === sanitizeLeagueId(this.leagueId);
        }
        return !!sanitizeLeagueId(this.leagueId);
      } catch {
        return false;
      }
    },

    dashboardsHref() {
      return '/admin/dashboard';
    },

    accountHref(hash = '') {
      const clean = String(hash || '').replace(/^#/, '').trim();
      return clean ? `/admin/account-settings#${clean}` : '/admin/account-settings';
    },

    isActive(h) {
      const p = norm(this.path);
      const want = norm(h);
      return p === want || p.startsWith(want + '/');
    },

    /** Account settings hub (league rules/builder embeds, layout, backup, subscription) + welcome wizard. */
    isAccountHubActive() {
      const p = norm(this.path);
      return (
        p === '/welcome' ||
        p.startsWith('/welcome/') ||
        p === '/account-settings' ||
        p.startsWith('/account-settings/') ||
        p === '/backup-sync' ||
        p.startsWith('/backup-sync/') ||
        p === '/dashboard-layout' ||
        p.startsWith('/dashboard-layout/') ||
        p === '/league-rules' ||
        p.startsWith('/league-rules/') ||
        p === '/league-builder' ||
        p.startsWith('/league-builder/')
      );
    },

    isDashboardHubActive() {
      const raw = String(globalThis.location.pathname || '').replace(/\/+$/, '') || '/';
      if (isLeaguePrefixedAdminPath(raw)) return false;
      const p = norm(this.path);
      return p === '/' || p === '/dashboard' || p.startsWith('/dashboard/');
    },

    setLeague(nextLeague) {
      const clean = sanitizeLeagueId(nextLeague);
      if (!clean) return;
      this.leaguePickerOpen = false;
      if (clean === sanitizeLeagueId(this.leagueId)) return;

      this.leagueId = clean;
      this.rememberLeague(clean);
      try {
        localStorage.setItem(LEAGUE_ID_KEY, clean);
      } catch {
        // ignore write failures
      }

      const next = this.leagueDetailHref(clean);
      const dispatchChanged = () => {
        try {
          globalThis.document?.dispatchEvent(
            new CustomEvent('dm:admin-league-changed', { detail: { leagueId: clean } })
          );
        } catch {
          // ignore
        }
      };

      const now = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
      if (now === next) {
        dispatchChanged();
        return;
      }
      if (globalThis.PineconeRouter && this.prefix === '/admin') {
        globalThis.PineconeRouter.navigate(next);
      } else {
        globalThis.location.assign(next);
      }
      dispatchChanged();
    },
  }));
}
